import { Button as ButtonPrimitive } from "@base-ui/react/button";
import type {
  HTMLAttributes,
  MouseEventHandler,
  ReactElement,
  ReactNode,
} from "react";
import { Children, isValidElement } from "react";
import { cn } from "@/lib/utils";
import type { Tone } from "@/types/tone";

const baseStyles = cn(
  "relative",
  "flex",
  "flex-col",
  "items-start",
  "w-full",
  "rounded-(--radius-sm)",
  "border",
  "border-outline-subtle",
  "bg-surface",
  "shadow-elevation-1",
);

const paddingStyles = {
  none: "p-0",
  compact: "p-(--spacing-12)",
  comfortable: "px-(--spacing-20) py-(--spacing-16)",
} as const;

const cardGroupStyles = cn(
  "flex",
  "w-full",
  "items-stretch",
  "[&>[data-slot=card-group-item]:first-child:not(:last-child)>[data-slot=card]]:rounded-r-none",
  "[&>[data-slot=card-group-item]:first-child:not(:last-child)>[data-slot=card]]:border-r-0",
  "[&>[data-slot=card-group-item]:not(:first-child):not(:last-child)>[data-slot=card]]:rounded-none",
  "[&>[data-slot=card-group-item]:not(:first-child):not(:last-child)>[data-slot=card]]:border-r-0",
  "[&>[data-slot=card-group-item]:last-child:not(:first-child)>[data-slot=card]]:rounded-l-none",
);

const cardGroupItemStyles = cn(
  "flex",
  "min-w-0",
  "flex-1",
  "[&>[data-slot=card]]:h-full",
);

const interactiveStyles = cn(
  "cursor-pointer",
  "select-none",
  "outline-none",
  "transition-colors",
  "duration-200",
  "ease-in-out",
  "text-left",
  "hover:bg-surface-hover",
  "active:bg-surface-container-highest",
  "focus-visible:ring-2",
  "focus-visible:ring-button-focus-ring",
  "focus-visible:ring-offset-1",
  "focus-visible:ring-offset-surface",
);

const contentStyles = cn(
  "flex",
  "w-full",
  "flex-col",
  "items-start",
  "overflow-hidden",
  "rounded-(--radius-sm)",
);

// ─── Shared slot sub-components ──────────────────────────────────────────────

export type BorderFillTone = Extract<
  Tone,
  "brand" | "positive" | "warning" | "negative" | "informative"
>;

const borderFillToneStyles: Record<BorderFillTone, string> = {
  brand: "bg-primary",
  positive: "bg-positive",
  warning: "bg-accessibility-warning",
  negative: "bg-negative",
  informative: "bg-informative",
};

interface BorderFillProps {
  tone: BorderFillTone;
}

/**
 * Optional decorative top border strip. Place as the first child of Card.
 * Accepts a tone to apply the appropriate semantic color.
 */
function CardBorderFill({ tone }: BorderFillProps) {
  return (
    <div
      data-slot="card-border-fill"
      className={cn(
        "pointer-events-none absolute -top-px -left-px -right-px z-10 h-(--spacing-8) rounded-tl-(--radius-sm) rounded-tr-(--radius-sm)",
        borderFillToneStyles[tone],
      )}
    />
  );
}

function isCardBorderFillChild(
  child: ReactNode,
): child is ReactElement<BorderFillProps> {
  return isValidElement(child) && child.type === CardBorderFill;
}

interface ImageSlotProps {
  children: ReactNode;
}

/**
 * Optional hero image area. Place after CardBorderFill (if present).
 */
function CardImageSlot({ children }: ImageSlotProps) {
  return (
    <div
      data-slot="card-image-slot"
      className="w-full shrink-0 overflow-hidden"
    >
      {children}
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

type StaticCardProps = Omit<HTMLAttributes<HTMLDivElement>, "className"> & {
  onClick?: undefined;
  disabled?: undefined;
} & CardVariantProps;

type InteractiveCardProps = Omit<ButtonPrimitive.Props, "className"> & {
  onClick: MouseEventHandler<HTMLButtonElement>;
} & CardVariantProps;

type CardPadding = keyof typeof paddingStyles;

interface CardVariantProps {
  /**
   * Controls internal card spacing. Defaults to "comfortable".
   */
  padding?: CardPadding;
  /**
   * Allows cards to fill their parent row height.
   */
  fillHeight?: boolean;
}

export type CardProps = StaticCardProps | InteractiveCardProps;

export interface CardGroupProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "className" | "children"> {
  children: ReactNode;
}

function CardGroup({ children, ...props }: CardGroupProps) {
  return (
    <div data-slot="card-group" className={cardGroupStyles} {...props}>
      {Children.map(children, (child) => (
        <div data-slot="card-group-item" className={cardGroupItemStyles}>
          {child}
        </div>
      ))}
    </div>
  );
}

/**
 * General-purpose card primitive.
 *
 * Renders as a `<div>` when static, or a `<button>` when `onClick` is provided.
 * Interactive cards get hover, focus, active, and disabled states automatically.
 *
 * Use `CardBorderFill` and `CardImageSlot` for optional decorative slots.
 *
 * @example
 * // Static
 * <Card padding="none">
 *   <CardImageSlot><img src={src} alt="" /></CardImageSlot>
 *   <div>Content</div>
 * </Card>
 *
 * // Interactive
 * <Card onClick={() => navigate(id)}>
 *   <CardBorderFill tone="positive" />
 *   <div>Content</div>
 * </Card>
 */
function Card({
  children,
  fillHeight = false,
  padding = "comfortable",
  ...props
}: CardProps) {
  const childArray = Children.toArray(children);
  const borderFillChildren = childArray.filter(isCardBorderFillChild);
  const hasBorderFill = borderFillChildren.length > 0;
  const contentChildren = childArray.filter(
    (child) => !isCardBorderFillChild(child),
  );
  const content = hasBorderFill ? (
    <div
      data-slot="card-content"
      className={cn(contentStyles, paddingStyles[padding])}
    >
      {contentChildren}
    </div>
  ) : (
    children
  );
  const heightClassName = fillHeight ? "h-full" : null;

  if ("onClick" in props && props.onClick !== undefined) {
    const { onClick, disabled, ...rest } = props as InteractiveCardProps;
    return (
      <ButtonPrimitive
        data-slot="card"
        className={cn(
          baseStyles,
          hasBorderFill ? "overflow-visible" : "overflow-hidden",
          !hasBorderFill && paddingStyles[padding],
          heightClassName,
          interactiveStyles,
          disabled && "opacity-50 pointer-events-none",
        )}
        onClick={onClick}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        {...rest}
      >
        {borderFillChildren}
        {content}
      </ButtonPrimitive>
    );
  }

  return (
    <div
      data-slot="card"
      className={cn(
        baseStyles,
        hasBorderFill ? "overflow-visible" : "overflow-hidden",
        !hasBorderFill && paddingStyles[padding],
        heightClassName,
      )}
      {...(props as StaticCardProps)}
    >
      {borderFillChildren}
      {content}
    </div>
  );
}

export { Card, CardBorderFill, CardGroup, CardImageSlot };
export type { BorderFillTone as CardBorderFillTone, CardPadding };
